import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneNumber } from '../../services/api';

interface LoginFormProps {
  onSuccess: () => void;
  onSwitchToRegister: () => void;
  onNeedsVerification: (phone: string) => void;
  onForgotPassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister, onNeedsVerification, onForgotPassword }) => {
  const { login, loading } = useAuth();
  const [formData, setFormData] = useState({
    phone_number: '',
    password: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Phone number validation - allow admin numbers
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else {
      const phoneRegex = /^(\+27|0)[0-9]{9}$/;
      const adminRegex = /^.*admin.*$/i; // Allow any number containing "admin"
      
      if (!phoneRegex.test(formData.phone_number.trim()) && !adminRegex.test(formData.phone_number.trim())) {
        newErrors.phone_number = 'Please enter a valid South African phone number or admin credentials';
      }
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const result = await login({
      phone_number: formatPhoneNumber(formData.phone_number.trim()),
      password: formData.password
    });

    if (result.success) {
      if (result.verification_required) {
        onNeedsVerification(formatPhoneNumber(formData.phone_number.trim()));
      } else {
        onSuccess();
      }
    } else {
      setErrors({ general: result.message });
    }
  };

  return (
    <div className="login-form">
      <div className="form-header">
        <h1>Welcome Back</h1>
        <p>Sign in to access your GenLend account</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form">
        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="phone_number">Phone Number</label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleInputChange}
            placeholder="0123456789"
            className={errors.phone_number ? 'error' : ''}
            disabled={loading}
          />
          {errors.phone_number && (
            <span className="error-message">{errors.phone_number}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              className={errors.password ? 'error' : ''}
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M21 4L3 20"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <span className="error-message">{errors.password}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={loading}
        >
          {loading ? (
            <span className="loading-spinner">
              <span className="dot"></span>
            </span>
          ) : (
            'Sign In'
          )}
        </button>

        <div className="form-footer">
          <button
            type="button"
            className="link-button"
            onClick={onForgotPassword}
          >
            Forgot your password?
          </button>
          
          <div className="switch-form">
            <span>Don't have an account? </span>
            <button
              type="button"
              className="link-button primary"
              onClick={onSwitchToRegister}
            >
              Sign Up
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
